import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { ErrorDistributionResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { semanticColors, chartChrome } from '../../utils/chartColors';
import './ErrorDistributionChart.scss';

interface ErrorDistributionChartProps {
  data: ErrorDistributionResponse | null;
  isLoading: boolean;
  onBarClick?: (errorCategory: string) => void;
  error?: Error | null;
  onRetry?: () => void;
}

const ErrorDistributionChart: FC<ErrorDistributionChartProps> = ({ data, isLoading, onBarClick, error, onRetry }) => {
  const { t } = useTranslation();
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.categories.length === 0) {
      return () => {};
    }

    // Filter out the "duplicate" error category
    const filtered = data.categories.filter((c) => c.errorCategory !== 'duplicate');
    if (filtered.length === 0) {
      return () => {};
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const categories = filtered.map((c) => c.errorCategory);
    const translatedCategories = categories.map(
      (c) => t(`cpi.ingestion.errorCategory.${c}`)
    );
    const counts = filtered.map((c) => c.count ?? 0);
    const percentages = filtered.map((c) => c.percentage ?? 0);
    const semantic = semanticColors();
    const chrome = chartChrome();

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const idx = params.dataIndex;
          if (idx == null || idx < 0 || idx >= categories.length) return '';
          return `${translatedCategories[idx]}: ${counts[idx]} (${percentages[idx]}%)`;
        },
      },
      grid: {
        left: '3%',
        right: '15%',
        top: '3%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: String(t('cpi.axis.errorCount')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { show: false },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: translatedCategories,
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: chrome.text,
          fontSize: 13,
        },
      },
      series: [
        {
          type: 'bar',
          data: counts.map((c, i) => {
            const pct = percentages[i];
            let color = semantic.good; // green for 0%
            if (pct > 5) color = semantic.bad; // red for >5%
            else if (pct > 0) color = semantic.warn; // yellow/orange for >0 and <=5%
            return { value: c, itemStyle: { color }, name: translatedCategories[i] };
          }),
          barMaxWidth: 32,
          itemStyle: {
            borderRadius: [0, 4, 4, 0],
          },
          barMinHeight: 6,
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => {
              const idx = params.dataIndex;
              return `${counts[idx]}`;
            },
            color: chrome.text,
            fontSize: 12,
          },
        },
      ],
    };

    chartInstance.current.setOption(option, true);

    // Click handler
    chartInstance.current.off('click');
    if (onBarClick) {
      chartInstance.current.on('click', (params: any) => {
        if (params.componentType === 'series' && params.dataIndex != null) {
          onBarClick(categories[params.dataIndex]);
        }
      });
    }

    return () => {
      chartInstance.current?.off('click');
    };
  }, [data, onBarClick, chartNode, t]);

  // Handle resize & cleanup
  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="error-distribution-chart">
        <h3 className="error-distribution-chart__title">{t('cpi.errorDistribution')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="error-distribution-chart error-distribution-chart--loading">
        <h3 className="error-distribution-chart__title">{t('cpi.errorDistribution')}</h3>
        <div className="error-distribution-chart__skeleton" />
      </div>
    );
  }

  const filteredCategories = data?.categories.filter((c) => c.errorCategory !== 'duplicate') ?? [];

  if (!data || filteredCategories.length === 0) {
    return (
      <div className="error-distribution-chart">
        <h3 className="error-distribution-chart__title">{t('cpi.errorDistribution')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  const chartHeight = Math.max(200, filteredCategories.length * 48);

  return (
    <div className="error-distribution-chart">
      <h3 className="error-distribution-chart__title">{t('cpi.errorDistribution')}</h3>
      <div
        ref={chartCallbackRef}
        style={{ height: `${chartHeight}px`, width: '100%', cursor: onBarClick ? 'pointer' : 'default' }}
      />
    </div>
  );
};

export default ErrorDistributionChart;
